// src/components/ConsentModal.jsx
import React, { useState, useEffect, useCallback } from 'react';

/**
 * Represents a consent purpose.
 * @typedef {Object} ConsentPurpose
 * @property {string} id - The unique identifier for the purpose (must match Zaraz Purpose Key).
 * @property {string} name - A user-friendly name for the purpose.
 * @property {string} description - A short description of what this purpose is for.
 * @property {boolean} [isEssential=false] - If true, this purpose is considered essential and cannot be opted out of.
 * @property {boolean} [defaultGranted=false] - The default consent state for toggles if not essential (used for initializing customization).
 */

/**
 * Helper function to get a cookie by name.
 * @param {string} name - The name of the cookie.
 * @returns {string | null} The cookie value or null if not found.
 */
function getCookie(name) {
  if (typeof document === 'undefined') return null; // Guard for SSR
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

/**
 * ConsentModal component.
 * @param {Object} props
 * @param {ConsentPurpose[]} props.purposes - An array of consent purpose objects.
 * @param {string} [props.privacyPolicyUrl="#"] - URL to your privacy policy.
 * @param {string} [props.cookiePolicyUrl="#"] - URL to your cookie policy.
 * @param {string} [props.zarazConsentCookieName="cf_consent"] - The name of the Zaraz consent cookie.
 */
const ConsentModal = ({
  purposes = [],
  privacyPolicyUrl = "#",
  cookiePolicyUrl = "#",
  zarazConsentCookieName = "cf_consent"
}) => {
  const [isVisible, setIsVisible] = useState(false); // Default to false, useEffect will manage showing it
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [currentPreferences, setCurrentPreferences] = useState({});

  const zarazIsReady = useCallback(() => {
    return typeof window !== 'undefined' && window.zaraz && window.zaraz.consent;
  }, []);

  // Initialize preferences when customization view is opened or purposes change
  useEffect(() => {
    if (isCustomizing) {
      const initialPrefs = {};
      purposes.forEach(purpose => {
        let currentConsentForPurpose = purpose.defaultGranted;
        if (zarazIsReady() && typeof window.zaraz.consent.get === 'function') {
          const zarazState = window.zaraz.consent.get(purpose.id);
          if (typeof zarazState === 'boolean') {
            currentConsentForPurpose = zarazState;
          }
        }
        initialPrefs[purpose.id] = purpose.isEssential || currentConsentForPurpose;
      });
      setCurrentPreferences(initialPrefs);
    }
  }, [isCustomizing, purposes, zarazIsReady]);

  // Check if modal should be displayed on component mount
  useEffect(() => {
    // console.log(`[Visibility Effect] Running. Current isVisible state: ${isVisible}. localStorage flag: ${localStorage.getItem('user_consent_interaction_v1')}`);
    let attemptTimer = null;

    const checkConsentAndShowModal = () => {
      if (localStorage.getItem('user_consent_interaction_v1') === 'true') {
        // console.info('[Visibility Effect] Local flag "user_consent_interaction_v1" is true. Modal should not show.');
        if (isVisible) setIsVisible(false); // Ensure it's hidden if flag is set
        return;
      }

      const consentCookie = getCookie(zarazConsentCookieName);
      // console.info(`[Visibility Effect] Value of '${zarazConsentCookieName}' cookie:`, consentCookie);

      if (zarazIsReady() && typeof window.zaraz.consent.get === 'function') {
        let hasActiveNonDefaultConsent = false;
        for (const purpose of purposes) {
          if (!purpose.isEssential) {
            const zarazState = window.zaraz.consent.get(purpose.id);
            // console.info(`[Visibility Effect] Zaraz state for ${purpose.id} ('${purpose.name}'):`, zarazState);
            if (zarazState === true) {
              hasActiveNonDefaultConsent = true;
              break;
            }
          }
        }

        if (hasActiveNonDefaultConsent) {
          // console.info('[Visibility Effect] Zaraz API indicates active non-default. Assuming prior interaction. Hiding modal.');
          setIsVisible(false);
          localStorage.setItem('user_consent_interaction_v1', 'true');
          return;
        }
        
        // If no prior interaction flag, no active non-default consent from API,
        // and cookie logic (if more robustly implemented) doesn't indicate prior choice, show modal.
        // For now, this simplified logic will show if the above conditions aren't met.
        // console.info('[Visibility Effect] No definitive prior active consent found. Attempting to show modal.');
        setIsVisible(true);

      } else if (!zarazIsReady()) {
        // console.info('[Visibility Effect] Zaraz not ready, retrying...');
        attemptTimer = setTimeout(checkConsentAndShowModal, 500);
      } else {
        // console.warn('[Visibility Effect] Zaraz ready but API issue or ambiguous state. Defaulting to show modal.');
        setIsVisible(true);
      }
    };

    // Only run the core check if the local flag isn't already true
    if (localStorage.getItem('user_consent_interaction_v1') !== 'true') {
      checkConsentAndShowModal();
    } else {
      // If flag is true, ensure modal is hidden (handles case where effect might run again)
      if (isVisible) setIsVisible(false);
    }

    return () => {
      if (attemptTimer) clearTimeout(attemptTimer);
    };
  // Removed isVisible from dependency array to prevent loops based on its own change
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [zarazIsReady, purposes, zarazConsentCookieName]); 


  const persistConsentDecision = () => {
    console.log('[Persist] Called. Setting localStorage. Attempting to hide modal by setting isVisible to false.');
    localStorage.setItem('user_consent_interaction_v1', 'true');
    setIsVisible(false);      // This should trigger re-render making the modal null
    setIsCustomizing(false);
    console.log('[Persist] isVisible state update scheduled to false.');
  };

  const handleAcceptAll = () => {
    console.log('[Action] handleAcceptAll called.');
    if (zarazIsReady()) {
      window.zaraz.consent.setAll(true);
      console.info('Zaraz: Consented to all purposes.');
      if (typeof window.zaraz.consent.sendQueuedEvents === 'function') {
        window.zaraz.consent.sendQueuedEvents();
      }
    } else {
      console.warn('Zaraz API not available to accept all.');
    }
    persistConsentDecision();
  };

  const handleNecessaryOnly = () => {
    console.log('[Action] handleNecessaryOnly called.');
    if (zarazIsReady()) {
      const prefsToSet = {};
      purposes.forEach(purpose => {
        if (!purpose.isEssential) { 
          prefsToSet[purpose.id] = false; 
        }
        // Essential purposes are implicitly active by Zaraz based on dashboard config
      });
      if (Object.keys(prefsToSet).length > 0) {
        window.zaraz.consent.set(prefsToSet);
        console.info('Zaraz: Non-essential purposes set to false.', prefsToSet);
      } else {
        console.info('Zaraz: No non-essential purposes to set for "Necessary Only".');
      }
      if (typeof window.zaraz.consent.sendQueuedEvents === 'function') {
        window.zaraz.consent.sendQueuedEvents();
      }
    } else {
      console.warn('Zaraz API not available to set necessary only.');
    }
    persistConsentDecision();
  };

  const handleShowCustomize = () => {
    console.log('[Action] handleShowCustomize called.');
    if (purposes.filter(p => !p.isEssential).length === 0) {
      console.warn("No non-essential purposes configured for customization. Defaulting to Accept All.");
      handleAcceptAll(); 
      return;
    }
    setIsCustomizing(true);
  };

  const handleTogglePreference = (purposeId) => {
    const purpose = purposes.find(p => p.id === purposeId);
    if (purpose && purpose.isEssential) return;

    setCurrentPreferences(prev => ({
      ...prev,
      [purposeId]: !prev[purposeId],
    }));
  };

  const handleSavePreferences = () => {
    console.log('[Action] handleSavePreferences called.');
    if (zarazIsReady()) {
      const prefsToSet = {};
      purposes.forEach(purpose => {
        if (!purpose.isEssential) { 
          prefsToSet[purpose.id] = !!currentPreferences[purpose.id]; 
        }
      });

      const afterZarazSet = () => {
        if (typeof window.zaraz.consent.sendQueuedEvents === 'function') {
          window.zaraz.consent.sendQueuedEvents();
        }
        persistConsentDecision();
      };

      if (Object.keys(prefsToSet).length > 0) {
        console.log("Attempting to set custom preferences:", prefsToSet);
        try {
          window.zaraz.consent.set(prefsToSet);
          console.info('Zaraz: Custom consent preferences saved.', prefsToSet);
          afterZarazSet();
        } catch (error) {
          console.error("Error during zaraz.consent.set:", error, prefsToSet);
          afterZarazSet(); // Still try to close modal and persist interaction
        }
      } else {
        console.info('Zaraz: No non-essential preferences to save. Essentials are implicitly active.');
        afterZarazSet();
      }
    } else {
      console.warn('Zaraz API not available to save preferences.');
      persistConsentDecision();
    }
  };

  if (!isVisible) {
    console.log('[Render] isVisible is false. Modal will not be rendered (returning null).');
    return null;
  }
  console.log('[Render] isVisible is true. Modal will be rendered.');

  return (
    <div className="modal modal-open modal-bottom sm:modal-middle" data-testid="consent-modal">
      <div className="modal-box relative prose max-w-none sm:max-w-lg md:max-w-xl lg:max-w-2xl">
        {!isCustomizing ? (
          <>
            <h3 className="font-bold text-lg mb-2">Your Privacy Choices</h3>
            <p className="py-2 text-sm">
              We use cookies and similar technologies to enhance your Browse experience,
              personalize content and ads, analyze traffic, and ensure security.
              By clicking "Accept All", you consent to our use of these technologies.
              You can change your preferences at any time. For more information, please see our
              {' '}<a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="link link-hover">Privacy Policy</a>
              {' '}and
              {' '}<a href={cookiePolicyUrl} target="_blank" rel="noopener noreferrer" className="link link-hover">Cookie Policy</a>.
            </p>
            <div className="modal-action mt-6 flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:justify-end">
              <button
                className="btn btn-ghost btn-sm sm:btn-md"
                onClick={handleShowCustomize}
                disabled={purposes.filter(p => !p.isEssential).length === 0}
              >
                Customize
              </button>
              <button className="btn btn-outline btn-sm sm:btn-md" onClick={handleNecessaryOnly}>
                Necessary Only
              </button>
              <button className="btn btn-primary btn-sm sm:btn-md" onClick={handleAcceptAll}>
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <button
                className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                onClick={() => {
                  console.log('[Action] Close customization view.');
                  setIsCustomizing(false);
                }}
                aria-label="Close customization"
            >
                ✕
            </button>
            <h3 className="font-bold text-lg mb-2">Customize Preferences</h3>
            <p className="py-2 text-sm">
              Manage your consent for different purposes below. Essential services cannot be disabled.
            </p>
            <div className="form-control space-y-1 my-4 max-h-60 overflow-y-auto pr-2">
              {purposes.map((purpose) => (
                <label key={purpose.id} className={`p-3 rounded-lg flex items-center justify-between transition-colors duration-150 ease-in-out ${purpose.isEssential ? 'opacity-70 cursor-not-allowed' : 'hover:bg-base-200 cursor-pointer'}`}>
                  <div className="flex-1 mr-2">
                    <div className="font-semibold text-base-content">{purpose.name}</div>
                    <div className="text-xs text-base-content/70">{purpose.description}</div>
                  </div>
                  <input
                    type="checkbox"
                    className={`toggle toggle-lg ${currentPreferences[purpose.id] && !purpose.isEssential ? 'toggle-primary' : ''} ${purpose.isEssential ? '' : ''}`}
                    checked={purpose.isEssential || !!currentPreferences[purpose.id]}
                    onChange={() => handleTogglePreference(purpose.id)}
                    disabled={purpose.isEssential}
                    aria-labelledby={`purpose-label-${purpose.id}`}
                  />
                  <span id={`purpose-label-${purpose.id}`} className="sr-only">{purpose.name}</span>
                </label>
              ))}
              {purposes.length === 0 && <p className="text-sm text-center">No customizable purposes defined.</p>}
            </div>
            <div className="modal-action mt-6 flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:justify-end">
              <button className="btn btn-ghost btn-sm sm:btn-md" onClick={() => {
                console.log('[Action] Back from customization.');
                setIsCustomizing(false);
              }}>
                Back
              </button>
              <button className="btn btn-primary btn-sm sm:btn-md" onClick={handleSavePreferences}>
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsentModal;